import { addQueryTimestamp, hasHistorySupport, remove } from '../utilities/utilities';
import { loadingStart, loadingEnd } from '../loader/loader';
import pubsub from '../pubsub/pubsub';
import axios from 'axios';

/**
 * Load more
 * A plugin that implements a "load more" pagination, with url updates and back-button support.
 * @see {@link http://tumbledry.org/2011/05/12/screw_hashbangs_building}
 * @version 3.0.0
 * @exports LoadMore
 */
export default class LoadMore {
    /**
     * Constructor
     * @param {Object} el - A DOM node.
     * @public
     */
    constructor(el) {
        /** Placeholder for base DOM element. */
        this.el = null;

        /** Placeholder for template (used if this plugin is used with json). */
        this.template = null;

        /** Target DOM id (the list). */
        this.targetId = null;

        /** Placeholder for target DOM element (the list). */
        this.targetEl = null;

        /** Boolean to indicate if there are any ajax-request in progress. */
        this.requestInProgress = false;

        /** 1 = DOESN'T WORK CORRECTLY RIGHT NOW. back button navigates WITHIN the page. 0 = back button navigates to previous, referring page. */
        this.supportBackButton = 0;

        /** Placeholder to contain the starting page (so url can display page=1-3). */
        this.urlCarryPageBase = null;

        /** Array that will contain the loaded pages. */
        this.itemCount = [];

        /** Selector for items. */
        this.itemSelector = 'article';

        /** Enables infinity scroll instead of visible load-more button **/
        this.infinity = false;

        /** The loader container element when using infinity scroll **/
        this.loaderEl = null;

        this._init(el);
    }

    /**
     * The initialization function for this module.
     * @param {Object} el - A DOM node.
     * @public
     */
    _init(el) {
        this.el = !el ? document.querySelector('[data-type="loadMore"]') : el;

        if (!(this.el && hasHistorySupport())) {
            return false;
        }

        this.targetId = this.el.getAttribute('aria-controls');
        this.targetEl = document.getElementById(this.targetId);

        if (!this.targetEl) {
            return false;
        }

        this.infinity = this.el.getAttribute('data-infinity') === 'true';

        const loaderId = this.el.getAttribute('data-loader');
        if (loaderId) {
            this.loaderEl = document.getElementById(loaderId);
        }

        const selector = this.el.getAttribute('data-itemselector');
        if (selector) {
            this.itemSelector = selector;
        }

        const templ = this.el.getAttribute('data-template');
        if (templ) {
            require([
                '../../templates/' + templ + '.hbs',
            ], tpl => {
                this.template = tpl;
                this._initEvents();
            });
        } else {
            this._initEvents();
        }
    }

    /**
     * Function that loads next page through ajax.
     * @private
     */
    _load() {
        let url = this.el.getAttribute('href');

        if (this.requestInProgress || !this._pathChange(url)) {
            return;
        }

        this.requestInProgress = true;

        const loadingMessage = this.el.getAttribute('data-loading-message') || 'Laddar';
        loadingStart(this.loaderEl || this.el, loadingMessage, '', true);

        //url = updateURLParameter(url, '_', getTimestamp());

        axios.get(addQueryTimestamp(url))
            .then(response => {
                const ct = (response.headers['content-type'] || '').match(/(html|json)/g)[0];

                switch (ct) {
                    case 'html': this._renderHtml(response.data); break;
                    case 'json': this._renderJson(response.data); break;
                }

                pubsub.publish('/ajaxForm/list/updated', { url, container: this.targetEl });
                pubsub.publish('/ajaxForm/sync/update/' + ct, doc);
            })
            .catch(() => { })
            .then(() => {
                this.requestInProgress = false;
                loadingEnd(this.loaderEl || this.el);
            });
    }

    /**
     * Function that renders new html-data.
     * @param {String} data - New data as a string of html.
     * @private
     */
    _renderHtml(data) {
        const doc = new DOMParser().parseFromString(data, 'text/html'),
            newPager = doc.querySelector('[data-type="loadMore"]'),
            newItems = doc.querySelectorAll('#' + this.targetId + ' ' + this.itemSelector);

        if (newItems.length) {
            this.targetEl.append(...newItems);
            (newItems[0].querySelector('a') || newItems[0]).focus();
        }

        this.itemCount.push(newItems.length);

        if (newPager) {
            this.el.setAttribute('href', newPager.getAttribute('href'))
        } else {
            remove(this.el);
        }

        pubsub.publish('/ajaxForm/sync/update/html', doc);
    }

    /**
     * Function that renders new json-data.
     * @param {String} data - New data as a string of json.
     * @private
     */
    _renderJson(data) {
        let html;

        try {
            html = this.template(data);
        } catch (e) {
            html = '';
        }

        if (data.pager && data.pager.pagerUrl) {
            this.el.classList.remove('btn--hidden');
            this.el.setAttribute('href', data.pager.pagerUrl);
        } else {
            this.el.classList.add('btn--hidden');
        }

        const tmp = document.createElement('div');
        tmp.innerHTML = html;

        const first = tmp.querySelector(this.itemSelector);

        this.targetEl.append(...tmp.children);

        if (first) {
            (first.querySelector('a') || first).focus();
        }

        pubsub.publish('/ajaxForm/sync/update/json', data);
    }

    /**
     * Function that updates the url.
     * @private
     */
    _pathChange(path) {

        const queryRegex = /[?&]pages?\=(?<p1>\d+)(?:\-(?<p2>\d+))?(?:\?|$)/;
        const pathRegex = /\/pages?\/(?<p1>\d+)(?:\-(?<p2>\d+))?\/?/;

        const pathParts = path.split('?');
        path = pathParts[0];
        const query = pathParts[1];

        // Try get pages from query
        let m = query && query.match(queryRegex);
        const useQuery = !!m;

        // Else try get pages from path
        if (!m) {
            m = path.match(pathRegex);
        }

        // No match
        if (!m) {
            return false;
        }

        if (!this.urlCarryPageBase) {
            const rx = useQuery ? queryRegex : pathRegex;
            const href = window.location.href.split('?')[0];
            let m;

            if ((m = href.match(rx))) {
                this.urlCarryPageBase = m.groups['p1'];
            } else if (m = (path.match(rx))) {
                this.urlCarryPageBase = m.groups['p1'] - 1;
            }
        }

        if (useQuery) {
            query = query.replace('page=', 'pages=' + this.urlCarryPageBase + '-');
        } else {
            path = path.replace('page/', 'pages/' + this.urlCarryPageBase + '-');
        }

        if (query) {
            path += "?" + query;
        }

        if (window.supportBackButton === 1) {
            window.history.pushState(null, null, path);
        } else {
            window.history.replaceState(null, null, path);
        }

        return true;
    }

    /**
     * Gets the scroll XY coordinates.
     * @private
     * @see {@link http://jsfiddle.net/8LpFR/}
     * @returns {Array} - The scroll XY coordinates.
     */
    _getScrollCoordinates() {
        let scrollX = 0,
            scrollY = 0;

        if (typeof window.pageYOffset == 'number') {
            //Netscape compliant
            scrollY = window.pageYOffset;
            scrollX = window.pageXOffset;
        } else if (
            document.body &&
            (document.body.scrollLeft || document.body.scrollTop)
        ) {
            //DOM compliant
            scrollY = document.body.scrollTop;
            scrollX = document.body.scrollLeft;
        } else if (
            document.documentElement &&
            (document.documentElement.scrollLeft ||
                document.documentElement.scrollTop)
        ) {
            //IE6 standards compliant mode
            scrollY = document.documentElement.scrollTop;
            scrollX = document.documentElement.scrollLeft;
        }
        return [scrollX, scrollY];
    }

    /**
     * Gets the document's height.
     * @private
     * @see {@link http://jsfiddle.net/8LpFR/}
     * @returns {Number} - The document height.
     */
    _getDocHeight() {
        const d = document;
        return Math.max(
            d.body.scrollHeight,
            d.body.offsetHeight,
            d.body.clientHeight,
            d.documentElement.scrollHeight,
            d.documentElement.offsetHeight,
            d.documentElement.clientHeight
        );
    }

    /**
     * Initiates events.
     * @private
     */
    _initEvents() {
        const footerStartPosition = document.body.scrollHeight - document.querySelector('footer').scrollHeight;

        // For infinity scroll
        if (this.infinity) {
            window.addEventListener('scroll', () => {
                if (this.el.classList.contains('btn--hidden')) {
                    return;
                }

                if ((this._getDocHeight() - (footerStartPosition / 5)) <= (this._getScrollCoordinates()[1] + window.innerHeight)) {
                    const lastDiv = this.targetEl.querySelector(this.itemSelector + ':last-child');
                    const lastDivOffset = lastDiv.offsetTop + lastDiv.clientHeight;
                    const pageOffset = window.pageYOffset + window.innerHeight;

                    if (pageOffset > lastDivOffset - 10) {
                        this._load();
                    }
                }
            });
            // For loadmore button
        } else {
            this.el.addEventListener('click', event => {
                event.preventDefault();
                this._load();
            });
        }

        if (this.supportBackButton === 1) {
            window.addEventListener('popstate', () => {
                if (this.supportBackButton === 1) {
                    const items = this.targetEl.querySelectorAll(this.itemSelector);
                    let lastTime = 0;

                    if (this.itemCount.length > 0) {
                        lastTime = this.itemCount.pop();
                    }

                    for (let i = 1; i <= lastTime; i++) {
                        this.targetEl.removeChild(items[items.length - i]);
                    }
                }
            });
        }
    }
}
